import React, { useMemo, useCallback, useState, useRef } from 'react';
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { graphBuilderApi } from '../../../services/api';
import {
  layoutProcessDefinition,
  applyExecutionStatus,
  applyEdgeStatus,
  computePhaseGroups,
} from '../ab/abLayoutEngine';
import '../ab/ABProcessMap.css';
import './CortexRuntimeGraph.css';
import NodeResultDetail from './NodeResultDetail';

function CortexNode({ data }) {
  const { typeLetter, label, status, isGate, executionType } = data;

  const statusClass = `ab-node-status-${status}`;
  let typeClass = 'ab-node-llm';
  if (isGate) typeClass = 'ab-node-gate';
  else if (executionType === 'snn_pipeline' || executionType === 'runtime_pipeline') typeClass = 'ab-node-snn';
  else if (executionType === 'python') typeClass = 'ab-node-python';
  else if (executionType === 'http') typeClass = 'ab-node-http';
  else if (executionType === 'case_extractor') typeClass = 'ab-node-case-extractor';
  else if (executionType === 'chain_reasoner') typeClass = 'ab-node-chain-reasoner';

  return (
    <div className={`ab-exec-node ${statusClass} ${typeClass}`} title={`${label}\n[${status}]`}>
      <Handle type="target" position={Position.Left} style={{ width: 6, height: 6, background: '#94a3b8', border: 'none' }} />
      <span className="ab-node-badge">{typeLetter}</span>
      <span className="ab-node-label">{label}</span>
      {status === 'completed' && <span className="ab-node-tick">&#10003;</span>}
      <Handle type="source" position={Position.Right} style={{ width: 6, height: 6, background: '#94a3b8', border: 'none' }} />
    </div>
  );
}

const nodeTypes = { abExecutionNode: CortexNode };

export default function CortexRuntimeGraph({ definition, nodeResults, diagnostics, executionId }) {
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [loadedResults, setLoadedResults] = useState({});
  const [nodeResultLoading, setNodeResultLoading] = useState(false);
  const loadingRef = useRef(null);

  const innerExecutionState = useMemo(() => {
    if (!nodeResults) return null;
    const completed = [];
    const failed = [];
    Object.entries(nodeResults).forEach(([id, r]) => {
      if (r?.status === 'completed') completed.push(id);
      else if (r?.status === 'failed') failed.push(id);
    });
    return {
      completed_nodes: completed,
      failed_nodes: failed,
      fired_gates: (definition?.gates || []).map(g => g.gate_id),
    };
  }, [nodeResults, definition]);

  const { layoutNodes, layoutEdges, phaseGroups } = useMemo(() => {
    if (!definition) return { layoutNodes: [], layoutEdges: [], phaseGroups: [] };
    const { nodes: ln, edges: le } = layoutProcessDefinition(definition);
    const pg = computePhaseGroups(definition, ln);
    return { layoutNodes: ln, layoutEdges: le, phaseGroups: pg };
  }, [definition]);

  const nodesWithStatus = useMemo(
    () => applyExecutionStatus(layoutNodes, innerExecutionState),
    [layoutNodes, innerExecutionState],
  );

  const edgesWithStatus = useMemo(
    () => applyEdgeStatus(layoutEdges, nodesWithStatus),
    [layoutEdges, nodesWithStatus],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(nodesWithStatus);
  const [edges, setEdges, onEdgesChange] = useEdgesState(edgesWithStatus);

  React.useEffect(() => { setNodes(nodesWithStatus); }, [nodesWithStatus, setNodes]);
  React.useEffect(() => { setEdges(edgesWithStatus); }, [edgesWithStatus, setEdges]);

  const handleNodeClick = useCallback((_event, node) => {
    if (node.data.isGate) return;
    const nid = node.id;
    setSelectedNodeId(nid);

    // Lazy-load the full node result if we only have a synthetic stub
    const existing = loadedResults[nid] || nodeResults?.[nid];
    if (executionId && (!existing?.outputs_produced)) {
      if (loadingRef.current === nid) return;
      loadingRef.current = nid;
      setNodeResultLoading(true);
      graphBuilderApi.getNodeResult(executionId, nid)
        .then((data) => {
          setLoadedResults(prev => ({ ...prev, [nid]: data }));
        })
        .catch((err) => {
          console.warn(`Failed to load result for ${nid}:`, err);
        })
        .finally(() => {
          setNodeResultLoading(false);
          loadingRef.current = null;
        });
    }
  }, [executionId, nodeResults, loadedResults]);

  const selectedNode = useMemo(() => {
    if (!selectedNodeId || !definition?.nodes) return null;
    return definition.nodes.find(n => n.node_id === selectedNodeId) || null;
  }, [selectedNodeId, definition]);

  const selectedResult = useMemo(() => {
    if (!selectedNodeId) return null;
    return loadedResults[selectedNodeId] || nodeResults?.[selectedNodeId] || null;
  }, [selectedNodeId, loadedResults, nodeResults]);

  if (!definition || !definition.nodes?.length) {
    return (
      <div className="cortex-runtime-empty">
        <p>No cortex runtime graph available. Run the process first, then select this tab.</p>
      </div>
    );
  }

  const stats = diagnostics || {};

  return (
    <div className="cortex-runtime-container">
      <div className="cortex-runtime-header">
        <h3>Cortex Runtime Pipeline</h3>
        <div className="cortex-runtime-stats">
          <span className="crt-stat">{definition.nodes.length} nodes</span>
          <span className="crt-stat">{(definition.synapses || []).length} synapses</span>
          <span className="crt-stat">{(definition.gates || []).length} gates</span>
          {stats.resolved_route && <span className="crt-stat">Route: {stats.resolved_route}</span>}
          {stats.posture && <span className="crt-stat">Posture: {stats.posture}</span>}
        </div>
      </div>

      <div className="cortex-runtime-graph">
        <div className="ab-process-map-container">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={true}
            fitView
            fitViewOptions={{ padding: 0.15, maxZoom: 1.5 }}
            minZoom={0.1}
            maxZoom={4}
            panOnScroll
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#e2e8f0" gap={24} variant="dots" />
            <Controls
              showInteractive={false}
              style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}
            />
            <MiniMap
              nodeColor={(n) => {
                const s = n.data?.status;
                if (s === 'completed') return '#16a34a';
                if (s === 'active') return '#22c55e';
                if (s === 'failed') return '#ef4444';
                if (s === 'skipped') return '#9ca3af';
                if (n.data?.executionType === 'snn_pipeline' || n.data?.executionType === 'runtime_pipeline') return '#0284c7';
                if (n.data?.executionType === 'python') return '#ea580c';
                if (n.data?.executionType === 'llm') return '#7c3aed';
                return '#d97706';
              }}
              maskColor="rgba(248,250,252,0.7)"
              style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8 }}
            />

            {phaseGroups.map((pg) => (
              <div
                key={pg.label}
                className="ab-phase-group"
                style={{
                  position: 'absolute',
                  left: pg.bounds.x,
                  top: pg.bounds.y,
                  width: pg.bounds.width,
                  height: pg.bounds.height,
                  pointerEvents: 'none',
                }}
              >
                <span className="phase-label">{pg.label}</span>
              </div>
            ))}
          </ReactFlow>
        </div>
      </div>

      {selectedNode && (
        <div className="cortex-runtime-detail">
          <NodeResultDetail
            node={selectedNode}
            result={selectedResult}
            historyEvents={[]}
            onClose={() => setSelectedNodeId(null)}
            loading={nodeResultLoading}
          />
        </div>
      )}
    </div>
  );
}
